import React, { createContext, use, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useApi, API_BASE_URL } from '../../api/useApi';
import { useAuthStatus } from '../../auth/useAuthStatus';

const STORAGE_KEY = 'adminSelectedCompany';

// Types for the organization/company
interface Organization {
  id: string;
  name: string;
  displayName: string;
  [key: string]: unknown;
}

interface AdminCompanyContextValue {
  companies: Organization[];
  selectedCompanyId: string | null;
  selectedCompany: Organization | null;
  selectCompany: (companyId: string | null) => void;
  clearCompany: () => void;
  isLoading: boolean;
}

interface AdminCompanyProviderProps {
  children: ReactNode;
}

const AdminCompanyContext = createContext<AdminCompanyContextValue | null>(null);

interface AccountsApiResponse {
  data: Array<{ id: string; name: string; status?: string; [key: string]: unknown }>;
  pagination: { page: number; per_page: number; total: number };
}

export const AdminCompanyProvider: React.FC<AdminCompanyProviderProps> = ({ children }) => {
  const { isPlatformUser, isAuthenticated } = useAuthStatus();
  const { get } = useApi();

  const [companies, setCompanies] = useState<Organization[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) || null;
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);

  /* eslint-disable @eslint-react/set-state-in-effect -- synchronous early-return + async fetch + stale selection cleanup */

  // Fetch real accounts from the API for platform users
  useEffect(() => {
    if (!isAuthenticated || !isPlatformUser) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchAccounts() {
      try {
        const result = await get<AccountsApiResponse>(
          `${API_BASE_URL}/api/admin/accounts?per_page=250&status=active`,
        );
        if (!cancelled) {
          setCompanies(
            result.data.map((a) => ({
              ...a,
              displayName: a.name,
            })),
          );
        }
      } catch (err) {
        console.error('Failed to load accounts for company selector', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchAccounts();
    return () => { cancelled = true; };
  }, [isAuthenticated, isPlatformUser, get]);

  // Persist selection to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (selectedCompanyId) {
        localStorage.setItem(STORAGE_KEY, selectedCompanyId);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [selectedCompanyId]);

  // Clear selection if the stored ID doesn't match any loaded company
  useEffect(() => {
    if (!isLoading && selectedCompanyId && companies.length > 0) {
      const exists = companies.some((c) => c.id === selectedCompanyId);
      if (!exists) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
        setSelectedCompanyId(null);
      }
    }
  }, [isLoading, selectedCompanyId, companies]);

  /* eslint-enable @eslint-react/set-state-in-effect */

  const selectedCompany = useMemo(() => {
    if (!selectedCompanyId) return null;
    return companies.find((org) => org.id === selectedCompanyId) || null;
  }, [selectedCompanyId, companies]);

  const selectCompany = useCallback((companyId: string | null) => {
    setSelectedCompanyId(companyId || null);
  }, []);

  const clearCompany = useCallback(() => {
    setSelectedCompanyId(null);
  }, []);

  const value: AdminCompanyContextValue = {
    companies,
    selectedCompanyId,
    selectedCompany,
    selectCompany,
    clearCompany,
    isLoading,
  };

  return (
    <AdminCompanyContext value={value}>
      {children}
    </AdminCompanyContext>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAdminCompany = (): AdminCompanyContextValue => {
  const context = use(AdminCompanyContext);
  if (!context) {
    throw new Error('useAdminCompany must be used within an AdminCompanyProvider');
  }
  return context;
};

export default AdminCompanyContext;
